import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment-timezone';
import styled from 'styled-components';

import Grid from '@material-ui/core/Grid';
import CircleLoader from '@invitation-homes/styled-ui/lib/components/Loader/CircleLoader';

import DayTimePicker from './DayTimePicker';
import ErrorMessage from '../components/ErrorMessage';
import SelfTourHeader from '../components/SelfTourHeader';
import FraudAwarenessBanner from '../components/FraudAwarenessBanner';
import { readSelectedSlot, removeAssignment, saveSelectedSlot } from '../services/scheduling-journey';

const WhiteGrid = styled(Grid)`
  padding-top: 10px;
  padding-right: 20px;
  padding-bottom: 40px;
  padding-left: 20px;
  background-color: white;
`;

export function buildPreviewAvailabilityEndpoint({ unitId, propertyId } = {}) {
  const path = '/self-tours/api/v2/availability/preview';
  const query = propertyId ? `property_id=${propertyId}` : `unit_id=${unitId}`;
  return `${path}?${query}`;
}

function getPropertyCode(unit) {
  return unit ? unit.property_code : undefined;
}

function getAvailabilityError(error) {
  return error.response?.data?.error || 'ERR_UNIT_NOT_AVAILABLE';
}

function persistSelectedTime(unitId, timeSelected) {
  const start = timeSelected?.start;
  if (!start) return { valid: false };
  return saveSelectedSlot(unitId, moment.utc(start).toISOString());
}

export class ScheduleShowingPublic extends Component {
  state = {
    loading: true,
    availability: null,
    daySelected: null,
    timeSelected: null,
    errorMessage: null,
  };

  componentDidMount() {
    const unitId = String(this.props.unitId);
    if (readSelectedSlot(unitId).valid) {
      this.props.history.replace(`/book-showing/${encodeURIComponent(unitId)}`);
      return;
    }

    this.loadPreviewAvailability();
  }

  loadPreviewAvailability = async () => {
    const { unit, unitId } = this.props;
    const endpoint = buildPreviewAvailabilityEndpoint({
      unitId,
      propertyId: getPropertyCode(unit),
    });

    try {
      const { data } = await axios.get(endpoint);
      this.setState({ availability: data, loading: false });
    } catch (error) {
      this.setState({
        errorMessage: getAvailabilityError(error),
        loading: false,
      });
    }
  };

  handleDateTimeData = (field, value) => {
    this.setState((current) => ({
      [field]: value && typeof value === 'object' ? { ...value } : value,
      ...(field === 'daySelected' && current.timeSelected ? { timeSelected: null } : {}),
    }));
  };

  handleContinue = () => {
    const unitId = String(this.props.unitId);
    const saved = persistSelectedTime(unitId, this.state.timeSelected);

    if (!saved.valid) {
      removeAssignment(unitId);
      window.location.replace(`/self-tours/schedule-showing/${encodeURIComponent(unitId)}`);
      return;
    }

    this.props.history.push(`/book-showing/${encodeURIComponent(unitId)}`);
  };

  render() {
    const { availability, daySelected, errorMessage, loading, timeSelected } = this.state;
    const { location, match, unitAddress, unitId } = this.props;

    if (errorMessage) {
      return <ErrorMessage type={errorMessage} unitId={{ unit_id: String(unitId) }} />;
    }

    if (loading) {
      return <CircleLoader size={24} />;
    }

    return (
      <Grid container xs={12} direction="row" alignItems="flex-start" justify="center">
        <Grid item xs={12} lg={10}>
          <SelfTourHeader />
        </Grid>
        <Fragment>
          <WhiteGrid item xs={12} lg={10} bg="white">
            <DayTimePicker
              onChange={this.handleDateTimeData}
              unitAddress={unitAddress}
              daySelected={daySelected}
              timeSelected={timeSelected}
              location={location}
              match={match}
              submitting={false}
              availability={availability}
              onContinue={this.handleContinue}
            />
          </WhiteGrid>
          <Grid item xs={12} lg={10}>
            <FraudAwarenessBanner
              onContactUsClick={() => this.setState({ errorMessage: 'FRAUD_AWARENESS_CONTACT_US' })}
            />
          </Grid>
        </Fragment>
      </Grid>
    );
  }
}

ScheduleShowingPublic.propTypes = {
  unitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ScheduleShowingPublic;
